import type { LinkAdapter, ProviderType } from './types'
import { plaidAdapter } from './plaid'
import { flinksAdapter } from './flinks'

/**
 * `ProviderType` → adapter. **The only place the two LINK-provider name
 * literals, `'plaid'` and `'flinks'`, may appear in `src/**` outside a test
 * or the generated schema** — `registry.test.ts`'s grep guard enforces
 * exactly that pair, no wider.
 *
 * **Correction (design gap-fill, 2026-09-17): the two paragraphs below used
 * to claim this file is "the only place a provider name appears" and that
 * "no call site outside this file ever compares a provider name" — both
 * false, found while adding `providerLabel` just beneath this docblock.
 * `useAccounts.ts:170` (`a.provider === 'manual'`, M5-R31's free-tier rule)
 * already compares a provider name outside this file, and the grep guard
 * does not catch it because it never claimed to: `PROVIDER_LITERAL` matches
 * `plaid`/`flinks` only. `'manual'` is not a LINK provider at all — it has
 * no `ProviderType` member and no adapter, because it means "no aggregator
 * connection to open" — so it was never this file's to own, and comparing
 * it elsewhere is not a violation of anything stated here. The true,
 * narrower claim: no call site outside this file compares account.provider
 * against `'plaid'` or `'flinks'` specifically; `'manual'` is a separate,
 * already-established exception, not a hole in the guard.**
 *
 * Reads `import.meta.env.VITE_FLAG_FLINKS` directly rather than through
 * `src/config/env.ts`'s `env.flags.flinks`. That module is not yet imported
 * anywhere in the app (its own docblock says so) and its `apiBase` field is
 * eager — `required('VITE_API_BASE')` throws at module evaluation whenever
 * ANYTHING imports it, which would make every test that touches this registry
 * (and everything downstream: `Connect.tsx`, `useAggregator.ts`) responsible
 * for stubbing an unrelated credential just to read one flag. Same local
 * pattern `env.ts` itself uses for every other flag, one line, no new
 * coupling.
 *
 * **`VITE_FLAG_FLINKS` gates registration, never a branch inside the flow.**
 * With it off, `getAdapter('flinks')` returns `undefined` and the picker's
 * own "no registered adapter" handling — required regardless of why an
 * adapter is missing — is what a Flinks catalogue entry meets.
 */
const FLINKS_ENABLED = import.meta.env.VITE_FLAG_FLINKS === 'true'

const ADAPTERS: Partial<Record<ProviderType, LinkAdapter>> = {
  plaid: plaidAdapter,
  ...(FLINKS_ENABLED ? { flinks: flinksAdapter } : {}),
}

export function getAdapter(provider: ProviderType): LinkAdapter | undefined {
  return ADAPTERS[provider]
}

/** Every adapter this build registers — for a guard that must see a NEW adapter
 *  the moment it is added, which a hand-written provider list cannot. */
export function registeredAdapters(): LinkAdapter[] {
  return Object.values(ADAPTERS)
}

/**
 * The registered adapter whose flow can navigate the browser fully away and
 * back (`redirectFlow`) — today Plaid's. A reconnect's `/link-session` has to
 * carry that adapter's `redirect_uri` BEFORE the session's own provider is
 * known: the backend checks one allow-list for every provider and a provider
 * with no redirect flow ignores the field, so sending it is harmless, and
 * asking "which adapter has a redirect flow" keeps every provider name in this
 * file.
 */
export function redirectFlowAdapter(): LinkAdapter | undefined {
  return Object.values(ADAPTERS).find((a) => a.redirectFlow !== undefined)
}

/**
 * Provider → the label the detail screen's "Data source" row shows for it
 * (design gap-fill, 2026-09-17). Takes a plain `string | null`, not
 * `ProviderType` — `AccountResponse.provider` (`schema.d.ts:3406`) is a bare
 * optional string on the wire, wider than the link-only union, and
 * `'manual'` (a real, common value — an account with no aggregator
 * connection at all) has no `ProviderType` member to be typed as.
 *
 * `null` for an absent OR unrecognised provider — the caller renders that as
 * an absent row, this module's established rule (`institution_name`/`type`'s
 * own subtype both follow it too), never a fabricated label for a value
 * this table does not know.
 */
const PROVIDER_LABELS: Record<string, string> = {
  manual: 'Manual entry',
  plaid: 'Plaid',
  flinks: 'Flinks',
}

export function providerLabel(provider: string | null): string | null {
  if (provider === null) return null
  return PROVIDER_LABELS[provider] ?? null
}

/**
 * The closed set `providerCode` returns (`manual`, `plaid`, `flinks`, else `null`) —
 * for `account_linked`-style events whose `provider` may legitimately be
 * `manual`. `aggregator_item_disconnected` is narrower and uses
 * `aggregatorProviderCode` below —
 * B3, architect review (#399). `Account.provider` is `string | null` straight
 * off the wire, and `assertValueSafe` (`analytics.ts`) only requires a
 * lower-case identifier under 32 characters for a `STANDALONE` string slot
 * with no `MEASUREMENTS`/`FLAGS` suffix — so an uncurated wire value (an
 * institution slug, a Plaid `institution_id`, anything else the backend ever
 * decides to put in this column) would reach the vendor with no guard
 * tripping. Narrowed through the SAME table `providerLabel` above already
 * owns, rather than a second literal comparison outside this file.
 *
 * `null` for an absent OR unrecognised value — `providerLabel`'s own rule,
 * restated rather than diverged from: never fabricate what this table does
 * not know. `null` also means "not measured" to `emit`, which is honest here
 * — an account whose provider this table cannot name is not the same fact as
 * "was manual: false".
 */
export function providerCode(provider: string | null): 'manual' | 'plaid' | 'flinks' | null {
  if (provider === null) return null
  return provider === 'manual' || provider === 'plaid' || provider === 'flinks' ? provider : null
}

/**
 * `aggregator_item_disconnected`'s `provider` is `plaid | flinks` only. A
 * manual connection cannot be disconnected from this screen, and an unknown or
 * absent provider is `null` ("not measured") rather than a guess — the
 * disconnect still happened, so the event still fires, without a fabricated
 * provider.
 */
export function aggregatorProviderCode(provider: string | null): 'plaid' | 'flinks' | null {
  const code = providerCode(provider)
  return code === 'plaid' || code === 'flinks' ? code : null
}
